/**
 * First-touch attribution: the UTM tags, landing path and referrer of the very first page
 * view, kept so the backend can store them when the account is created.
 *
 * Kept in localStorage and in a first-party cookie on `.wlth.xyz`, so it survives moving
 * between wlth.xyz subdomains and new tabs. An existing first touch is never overwritten.
 */

export const FIRST_TOUCH_STORAGE_KEY = 'wlth_first_touch'
export const FIRST_TOUCH_COOKIE = 'wlth_first_touch'

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
const SHARED_COOKIE_DOMAIN = 'wlth.xyz'

// Limits on the URL-encoded length, so the whole cookie stays well under the browser's 4 KB limit
// even when tags are full of non-Latin characters.
const MAX_ENCODED_UTM_LENGTH = 150
const MAX_ENCODED_URL_LENGTH = 400

export interface FirstTouch {
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  utmTerm: string | null
  /** Path only, never the query string, which can carry emails or codes. */
  landingPath: string
  /** Referrer with its query string and hash removed. */
  referrer: string | null
  /** ISO time of the first page view, from the browser clock. */
  firstSeenAt: string
}

export interface FirstTouchEnv {
  location: Pick<Location, 'pathname' | 'search' | 'hostname' | 'protocol'>
  referrer: string
  storage: Pick<Storage, 'getItem' | 'setItem'> | null
  getCookies: () => string
  setCookie: (cookie: string) => void
  now: () => Date
}

const encodedLength = (value: string) => {
  try {
    return encodeURIComponent(value).length
  } catch {
    return Number.POSITIVE_INFINITY
  }
}

/** Cuts a value, on whole characters, until its URL-encoded form fits in `maxEncoded`. */
export const clipEncoded = (value: string, maxEncoded: number): string => {
  if (encodedLength(value) <= maxEncoded) return value
  const chars = Array.from(value).slice(0, maxEncoded)
  while (chars.length > 0 && encodedLength(chars.join('')) > maxEncoded) chars.pop()
  return chars.join('')
}

const utmParam = (params: URLSearchParams, key: string): string | null => {
  const value = params.get(key)?.trim()
  return value ? clipEncoded(value, MAX_ENCODED_UTM_LENGTH) || null : null
}

export const stripUrl = (url: string): string | null => {
  if (!url) return null
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    return clipEncoded(`${parsed.origin}${parsed.pathname}`, MAX_ENCODED_URL_LENGTH)
  } catch {
    return null
  }
}

export const buildFirstTouch = (
  location: Pick<Location, 'pathname' | 'search'>,
  referrer: string,
  now: Date,
): FirstTouch => {
  const params = new URLSearchParams(location.search)
  return {
    utmSource: utmParam(params, 'utm_source'),
    utmMedium: utmParam(params, 'utm_medium'),
    utmCampaign: utmParam(params, 'utm_campaign'),
    utmContent: utmParam(params, 'utm_content'),
    utmTerm: utmParam(params, 'utm_term'),
    landingPath: clipEncoded(location.pathname || '/', MAX_ENCODED_URL_LENGTH) || '/',
    referrer: stripUrl(referrer),
    firstSeenAt: now.toISOString(),
  }
}

const isNullableString = (value: unknown) => value === null || typeof value === 'string'

export const parseFirstTouch = (raw: string | null | undefined): FirstTouch | null => {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as Record<string, unknown>
    const valid =
      typeof value === 'object' &&
      value !== null &&
      typeof value.landingPath === 'string' &&
      typeof value.firstSeenAt === 'string' &&
      !Number.isNaN(Date.parse(value.firstSeenAt)) &&
      [
        'utmSource',
        'utmMedium',
        'utmCampaign',
        'utmContent',
        'utmTerm',
        'referrer',
      ].every((key) => isNullableString(value[key]))
    return valid ? (value as unknown as FirstTouch) : null
  } catch {
    return null
  }
}

export const readCookie = (cookies: string, name: string): string | null => {
  const prefix = `${name}=`
  const match = cookies
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
  if (!match) return null
  try {
    return decodeURIComponent(match.slice(prefix.length))
  } catch {
    return null
  }
}

/** `.wlth.xyz` on any wlth.xyz host, so every subdomain shares it; host-only elsewhere (localhost, previews). */
export const cookieDomainFor = (hostname: string): string | null =>
  hostname === SHARED_COOKIE_DOMAIN || hostname.endsWith(`.${SHARED_COOKIE_DOMAIN}`)
    ? `.${SHARED_COOKIE_DOMAIN}`
    : null

export const serializeFirstTouchCookie = (
  firstTouch: FirstTouch,
  location: Pick<Location, 'hostname' | 'protocol'>,
): string => {
  const domain = cookieDomainFor(location.hostname)
  return [
    `${FIRST_TOUCH_COOKIE}=${encodeURIComponent(JSON.stringify(firstTouch))}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    domain ? `Domain=${domain}` : null,
    location.protocol === 'https:' ? 'Secure' : null,
  ]
    .filter(Boolean)
    .join('; ')
}

const safely = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn()
  } catch {
    return fallback
  }
}

const writeStorage = (env: FirstTouchEnv, firstTouch: FirstTouch) =>
  safely(
    () => env.storage?.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(firstTouch)),
    undefined,
  )

const writeCookie = (env: FirstTouchEnv, firstTouch: FirstTouch) =>
  safely(
    () => env.setCookie(serializeFirstTouchCookie(firstTouch, env.location)),
    undefined,
  )

/**
 * The stored first touch, or null. The cookie wins because it is shared across subdomains;
 * whichever store is missing is filled from the other.
 */
export const readFirstTouch = (
  env: FirstTouchEnv | null = browserEnv(),
): FirstTouch | null => {
  if (!env) return null
  const fromCookie = parseFirstTouch(
    safely(() => readCookie(env.getCookies(), FIRST_TOUCH_COOKIE), null),
  )
  const fromStorage = parseFirstTouch(
    safely(() => env.storage?.getItem(FIRST_TOUCH_STORAGE_KEY) ?? null, null),
  )

  if (fromCookie && !fromStorage) writeStorage(env, fromCookie)
  if (fromStorage && !fromCookie) writeCookie(env, fromStorage)

  return fromCookie ?? fromStorage
}

/** Records the first touch on the first page view. Never overwrites one that already exists. */
export const captureFirstTouch = (
  env: FirstTouchEnv | null = browserEnv(),
): FirstTouch | null => {
  if (!env) return null
  const existing = readFirstTouch(env)
  if (existing) return existing

  const firstTouch = buildFirstTouch(env.location, env.referrer, env.now())
  writeStorage(env, firstTouch)
  writeCookie(env, firstTouch)
  return firstTouch
}

export function browserEnv(): FirstTouchEnv | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null
  return {
    location: window.location,
    referrer: document.referrer,
    storage: safely(() => window.localStorage, null),
    getCookies: () => document.cookie,
    setCookie: (cookie) => {
      document.cookie = cookie
    },
    now: () => new Date(),
  }
}

/** The body the backend's Privy login accepts. Keys are snake-cased by the HTTP client. */
export const toLoginAttribution = (firstTouch: FirstTouch | null) =>
  firstTouch
    ? {
        utmSource: firstTouch.utmSource,
        utmMedium: firstTouch.utmMedium,
        utmCampaign: firstTouch.utmCampaign,
        utmContent: firstTouch.utmContent,
        utmTerm: firstTouch.utmTerm,
        landingPath: firstTouch.landingPath,
        referrer: firstTouch.referrer,
        firstSeenAt: firstTouch.firstSeenAt,
      }
    : undefined
